import gsap from 'gsap'

import {
  fadeRevealAnimation,
  getScrollTriggerOptions,
  linesRevealAnimation,
  type SharedAnimation
} from '@/components/sections/animations/shared'

import { Animation } from '@/types'

/**
 * Crafted To Matter — layout recomposition animation
 *
 * Images: each stays in its column; image_2 and image_3 align their bottom
 *         edge to the bottom edge of image_1.
 *
 * Titles: three words ("Crafted", "To", "Matter") leave their grid positions
 *         and assemble into a single line reading "Crafted To Matter".
 *         "Matter" stays at its X, only moves in Y; "To" aligns left of
 *         "Matter"; "Crafted" aligns left of "To".
 *         "Matter" receives a border-bottom that fills left→right.
 *
 * Bottom: text + CTA fade in.
 */
export const craftedToMatterAnimationDesktop = ({
  node,
  context,
  reverse,
  delay = 0
}: Animation) => {
  const selector = context?.selector || gsap.utils.selector(node)

  const grid = selector('[data-motion-grid]')[0] as HTMLElement
  const image1 = selector('[data-motion-image-1]')[0] as HTMLElement
  const image2 = selector('[data-motion-image-2]')[0] as HTMLElement
  const image3 = selector('[data-motion-image-3]')[0] as HTMLElement
  const title1 = selector('[data-motion-title-1]')[0] as HTMLElement
  const title2 = selector('[data-motion-title-2]')[0] as HTMLElement
  const title3 = selector('[data-motion-title-3]')[0] as HTMLElement
  const textEl = selector('[data-motion-text]')[0] as HTMLElement
  const ctaEl = selector('[data-motion-cta]')[0] as HTMLElement

  if (!grid || !image1 || !title1 || !title2 || !title3) return

  // ─── helpers ───────────────────────────────────────────────────────
  const rect = (el: HTMLElement) => el.getBoundingClientRect()

  /**
   * Compute the x/y offsets needed to move each element to its target
   * position. We run this inside onRefresh so values stay correct on resize.
   */
  const getOffsets = () => {
    const r1 = rect(image1)
    const r2 = rect(image2)
    const r3 = rect(image3)

    // ── Images: align bottom edges to image_1 bottom ──
    const img2dy = r1.bottom - r2.bottom
    const img3dy = r1.bottom - r3.bottom

    // ── Titles: assemble into one line ──
    // title_3 ("Matter") stays at its X, only moves in Y.
    // title_2 ("To") aligns to the left of title_3.
    // title_1 ("Crafted") aligns to the left of title_2.
    const t1 = rect(title1)
    const t2 = rect(title2)
    const t3 = rect(title3)

    // Space between words (mimics a natural whitespace gap)
    const wordGap = parseFloat(getComputedStyle(title1).fontSize) * 0.35

    // Target baseline Y: all words align to title_1's top Y
    const targetY = t1.top

    // title_3 keeps its X position, only moves in Y
    const title3dy = targetY - t3.top

    // title_2 aligns to the left of title_3
    const targetX2 = t3.left - wordGap - t2.width
    const title2dx = targetX2 - t2.left
    const title2dy = targetY - t2.top

    // title_1 aligns to the left of title_2
    const targetX1 = targetX2 - wordGap - t1.width
    const title1dx = targetX1 - t1.left
    const title1dy = targetY - t1.top

    return {
      img2dy,
      img3dy,
      title1dx,
      title1dy,
      title2dx,
      title2dy,
      title3dy
    }
  }

  // ─── main scrub timeline ──────────────────────────────────────────
  let offsets = getOffsets()

  // Sub-timelines so we can rebuild them on refresh
  const imgTl = gsap.timeline()
  const titleTl = gsap.timeline()
  const borderTl = gsap.timeline()

  // Border-bottom pseudo-element for "Matter": we add a real element
  const underline = document.createElement('span')
  underline.style.cssText =
    'position:absolute;bottom:-.2em;left:0;width:100%;height:.075em;background:currentColor;transform-origin:left;transform:scaleX(0);pointer-events:none;'
  title3.style.position = 'relative'
  title3.appendChild(underline)

  const buildTweens = () => {
    offsets = getOffsets()

    // ── images ──
    imgTl.clear()
    if (image2) {
      imgTl.to(image2, { y: offsets.img2dy, duration: 1, ease: 'none' }, 0)
    }
    if (image3) {
      imgTl.to(image3, { y: offsets.img3dy, duration: 1, ease: 'none' }, 0)
    }

    // ── titles ──
    titleTl.clear()
    titleTl.to(
      title1,
      {
        x: offsets.title1dx,
        y: offsets.title1dy,
        duration: 1,
        ease: 'power3.out'
      },
      0
    )
    titleTl.to(
      title2,
      {
        x: offsets.title2dx,
        y: offsets.title2dy,
        duration: 1,
        ease: 'power3.out'
      },
      0
    )
    titleTl.to(
      title3,
      {
        y: offsets.title3dy,
        duration: 1,
        ease: 'power3.out'
      },
      0
    )

    // ── border-bottom on "Matter" ──
    borderTl.clear()
    borderTl.to(underline, { scaleX: 1, duration: 0.7, ease: 'power3.inOut' }, 0)
  }

  buildTweens()

  const mainTl = gsap.timeline({
    scrollTrigger: {
      trigger: grid,
      start: 'top 70%',
      end: 'bottom -10%',
      scrub: 1,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onRefreshInit: () => {
        // Reset inline transforms before recalculating
        gsap.set([image2, image3, title1, title2, title3], {
          clearProps: 'x,y'
        })
      },
      onRefresh: () => {
        const progress = mainTl.progress()
        mainTl.progress(0)
        buildTweens()
        mainTl.invalidate()
        mainTl.progress(progress)
      }
    }
  })

  // Sequence: images + titles move together, then border draws
  mainTl.add(imgTl, 0)
  mainTl.add(titleTl, 0)
  mainTl.add(borderTl, 0.2) // border starts towards the end of the scrub

  // ─── bottom section reveal (text + CTA) — once ───────────────────
  const animations: SharedAnimation[] = []
  let textTl: gsap.core.Timeline | undefined
  let ctaTl: gsap.core.Timeline | undefined

  if (textEl) {
    const scrollOpts = getScrollTriggerOptions(textEl, reverse)
    textTl = gsap.timeline({ ...scrollOpts, delay })
    const anim = linesRevealAnimation(textEl, reverse)
    textTl.add(anim.timeline, 0)
    animations.push(anim)
  }

  if (ctaEl) {
    const scrollOpts = getScrollTriggerOptions(ctaEl, reverse)
    ctaTl = gsap.timeline({ ...scrollOpts, delay: delay + 0.1 })
    const anim = fadeRevealAnimation(ctaEl)
    ctaTl.add(anim.timeline, 0)
    animations.push(anim)
  }

  // ─── cleanup ──────────────────────────────────────────────────────
  return () => {
    mainTl.scrollTrigger?.kill()
    mainTl.kill()
    textTl?.scrollTrigger?.kill()
    textTl?.kill()
    ctaTl?.scrollTrigger?.kill()
    ctaTl?.kill()
    animations.forEach((a) => a.revert())

    // Remove the injected underline element
    underline.remove()
    title3.style.position = ''
  }
}

/**
 * Crafted To Matter — mobile animation
 *
 * Titles: scrub-based horizontal movement
 *   - title_1 starts aligned right, moves to xPercent: -40
 *   - title_2 starts aligned center, moves to xPercent: +10
 *   - title_3 starts aligned left, moves to xPercent: +30
 *
 * Text + CTA: once-triggered linesReveal / fadeReveal.
 */
export const craftedToMatterAnimationMobile = ({
  node,
  context,
  reverse,
  delay = 0
}: Animation) => {
  const selector = context?.selector || gsap.utils.selector(node)

  const grid = selector('[data-motion-grid]')[0] as HTMLElement
  const title1 = selector('[data-motion-title-1]')[0] as HTMLElement
  const title2 = selector('[data-motion-title-2]')[0] as HTMLElement
  const title3 = selector('[data-motion-title-3]')[0] as HTMLElement
  const textEl = selector('[data-motion-text]')[0] as HTMLElement
  const ctaEl = selector('[data-motion-cta]')[0] as HTMLElement
  const image = selector('[data-motion-image-1]')[0] as HTMLElement

  if (!grid || !title1 || !title2 || !title3 || !image) return

  // ─── titles scrub timeline ────────────────────────────────────────
  const titleTl = gsap.timeline()

  titleTl.fromTo(
    title1,
    { xPercent: 0 },
    { xPercent: -40, duration: 1, ease: 'none' },
    0
  )
  titleTl.fromTo(
    title2,
    { xPercent: 0 },
    { xPercent: 10, duration: 1, ease: 'none' },
    0
  )
  titleTl.fromTo(
    title3,
    { xPercent: 0 },
    { xPercent: 30, duration: 1, ease: 'none' },
    0
  )

  const mainTl = gsap.timeline({
    scrollTrigger: {
      trigger: grid,
      start: 'top 60%',
      end: 'bottom 40%',
      scrub: 1,
      invalidateOnRefresh: true
    }
  })

  mainTl.add(titleTl, 0)

  // ─── bottom section reveal (text + CTA) — once ───────────────────
  const animations: SharedAnimation[] = []
  let textTl: gsap.core.Timeline | undefined
  let ctaTl: gsap.core.Timeline | undefined

  if (image) {
    const scrollOpts = getScrollTriggerOptions(image, reverse)
    const anim = fadeRevealAnimation(image)
    const imgTl = gsap.timeline({ ...scrollOpts, delay })
    imgTl.add(anim.timeline, 0)
    animations.push(anim)
  }

  if (textEl) {
    const scrollOpts = getScrollTriggerOptions(textEl, reverse)
    textTl = gsap.timeline({ ...scrollOpts, delay })
    const anim = linesRevealAnimation(textEl, reverse)
    textTl.add(anim.timeline, 0)
    animations.push(anim)
  }

  if (ctaEl) {
    const scrollOpts = getScrollTriggerOptions(ctaEl, reverse)
    ctaTl = gsap.timeline({ ...scrollOpts, delay: delay + 0.1 })
    const anim = fadeRevealAnimation(ctaEl)
    ctaTl.add(anim.timeline, 0)
    animations.push(anim)
  }

  // ─── cleanup ──────────────────────────────────────────────────────
  return () => {
    mainTl.scrollTrigger?.kill()
    mainTl.kill()
    textTl?.scrollTrigger?.kill()
    textTl?.kill()
    ctaTl?.scrollTrigger?.kill()
    ctaTl?.kill()
    animations.forEach((a) => a.revert())
  }
}
